/**
 * POSHoldDialogs — three hold-related dialogs extracted from POSPage.js:
 *   POSHoldSaleDialog   — capture label before holding a cart
 *   POSHeldSalesDialog  — list/recall/delete held carts
 *   POSRecallConfirmDialog — confirm replace or auto-hold-and-recall
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Pause, ListRestart, Trash2, AlertTriangle } from 'lucide-react';
import { fmtCurrency } from '../../lib/localeUtils';

// ─── Hold Sale Dialog ────────────────────────────────────────────────────────
export function POSHoldSaleDialog({ open, onOpenChange, holdLabel, setHoldLabel, onConfirm, cartLength, total, customerName }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setHoldLabel(''); }}>
      <DialogContent className="max-w-sm" data-testid="hold-sale-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="h-4 w-4 text-primary" /> Hold Transaction
          </DialogTitle>
          <DialogDescription>
            Pause this cart so you can serve another customer. Add a note to find it later (e.g. "Mr. Smith - waiting for cash").
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Reference / note</Label>
            <Input
              value={holdLabel}
              onChange={(e) => setHoldLabel(e.target.value)}
              placeholder="e.g. John Doe — getting cash"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(); }}
              data-testid="hold-label-input"
            />
          </div>
          <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Items</span><span className="font-medium">{cartLength}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-semibold">{fmtCurrency(total)}</span></div>
            {customerName && (
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium truncate ml-2">{customerName}</span></div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-row gap-2">
          <Button variant="ghost" className="flex-1" onClick={() => onOpenChange(false)} data-testid="hold-cancel-btn">
            Cancel
          </Button>
          <Button className="flex-1 gap-1.5" onClick={onConfirm} data-testid="hold-confirm-btn">
            <Pause className="h-4 w-4" /> Hold Sale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Held Sales Dialog ───────────────────────────────────────────────────────
export function POSHeldSalesDialog({ open, onOpenChange, heldSales, onRecall, onDelete }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="held-sales-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListRestart className="h-4 w-4 text-primary" /> Held Transactions
            {heldSales.length > 0 && <Badge variant="secondary" className="ml-1 text-[10px]">{heldSales.length}</Badge>}
          </DialogTitle>
          <DialogDescription>
            Recall a paused cart to continue checkout, or remove ones you no longer need.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[55vh] -mx-1 px-1">
          {heldSales.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="held-empty">
              <Pause className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">No held transactions</p>
              <p className="text-xs mt-1">Use the Hold button on the cart to pause a sale.</p>
            </div>
          ) : (
            <div className="space-y-2 py-1">
              {heldSales.map((h) => {
                const items = h.cart || [];
                const sub = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
                const heldTotal = Math.max(0, sub - (Number(h.discount) || 0));
                const when = new Date(h.ts);
                const ago = Math.max(0, Math.round((Date.now() - when.getTime()) / 60000));
                return (
                  <div
                    key={h.id}
                    className="rounded-lg border bg-card p-3 flex items-start gap-3 hover:border-primary/40 transition-colors"
                    data-testid={`held-item-${h.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-semibold truncate">{h.label || 'Held sale'}</p>
                        <Badge variant="outline" className="text-[10px]">{items.length} item{items.length !== 1 ? 's' : ''}</Badge>
                        {h.customerCategory === 'insurance' && <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-300">Insurance</Badge>}
                        {h.customerCategory === 'corporate' && <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">Corporate</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {items.slice(0, 3).map(it => it.product_name).join(', ')}
                        {items.length > 3 ? ` +${items.length - 3} more` : ''}
                      </p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                        <span className="tabular-nums font-semibold text-foreground">{fmtCurrency(heldTotal)}</span>
                        <span>•</span>
                        <span>{ago === 0 ? 'just now' : `${ago} min ago`}</span>
                        {h.customerName && (<><span>•</span><span className="truncate">{h.customerName}</span></>)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        onClick={() => onRecall(h.id)}
                        data-testid={`recall-btn-${h.id}`}
                      >
                        <ListRestart className="h-3.5 w-3.5" /> Recall
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 text-muted-foreground hover:text-destructive"
                        onClick={() => onDelete(h.id)}
                        data-testid={`held-delete-btn-${h.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="held-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Recall Confirm Dialog ───────────────────────────────────────────────────
export function POSRecallConfirmDialog({ pendingRecallId, onCancel, onHoldAndRecall, onReplace }) {
  return (
    <Dialog open={!!pendingRecallId} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="recall-overwrite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Replace current cart?
          </DialogTitle>
          <DialogDescription>
            You already have items in the active cart. Recalling this held sale will replace them. Hold the current cart first if you want to keep it.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row gap-2">
          <Button variant="ghost" className="flex-1" onClick={onCancel} data-testid="recall-cancel-btn">
            Cancel
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-1.5"
            onClick={() => onHoldAndRecall(pendingRecallId)}
            data-testid="recall-hold-current-btn"
          >
            <Pause className="h-4 w-4" /> Hold current first
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => onReplace(pendingRecallId)}
            data-testid="recall-overwrite-confirm-btn"
          >
            Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
